import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useRules } from '../context/RuleContext';

// Tooltip component showing full rule details
function RuleDetailTooltip({ rule, children }) {
  const content = (
    <Paper sx={{ p: 2, maxWidth: 360 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Rule #{rule.sequenceNum} — Full Details
      </Typography>
      <Divider sx={{ mb: 1 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: '0.8rem' }}>
        <Typography variant="caption" color="text.secondary">Carrier:</Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>{rule.carrier}</Typography>

        <Typography variant="caption" color="text.secondary">Origin:</Typography>
        <Typography variant="caption">{rule.origin || 'Worldwide'}</Typography>

        <Typography variant="caption" color="text.secondary">Destination:</Typography>
        <Typography variant="caption">{rule.destination || 'Worldwide'}</Typography>

        <Typography variant="caption" color="text.secondary">Fee:</Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {rule.suppress === 'true' ? 'SUPPRESS' : ''}
          {rule.feePercent != null ? `${rule.feePercent}%` : ''}
          {rule.feeAmount ? ` ${rule.feeAmount}` : ''}
          {rule.oncePerBookingFeeAmount ? `OPB: ${rule.oncePerBookingFeeAmount}` : ''}
          {!rule.suppress && !rule.feePercent && !rule.feeAmount && !rule.oncePerBookingFeeAmount ? '—' : ''}
        </Typography>

        <Typography variant="caption" color="text.secondary">Trip Type:</Typography>
        <Typography variant="caption">{rule.tripType || 'Any'}</Typography>

        <Typography variant="caption" color="text.secondary">Cabin:</Typography>
        <Typography variant="caption">{rule.cabinClass || 'Any'}</Typography>

        <Typography variant="caption" color="text.secondary">Passenger:</Typography>
        <Typography variant="caption">{rule.passengerType || 'Any'}</Typography>

        <Typography variant="caption" color="text.secondary">Price Source:</Typography>
        <Typography variant="caption">{rule.priceSource || 'Any'}</Typography>

        <Typography variant="caption" color="text.secondary">Interline:</Typography>
        <Typography variant="caption">{rule.interline === 'true' ? 'Yes' : rule.interline === 'false' ? 'No' : 'Any'}</Typography>

        <Typography variant="caption" color="text.secondary">Slice Interline:</Typography>
        <Typography variant="caption">{rule.sliceInterline === 'true' ? 'Yes' : rule.sliceInterline === 'false' ? 'No' : 'Any'}</Typography>

        <Typography variant="caption" color="text.secondary">Min Segments:</Typography>
        <Typography variant="caption">{rule.minSegments || '—'}</Typography>

        <Typography variant="caption" color="text.secondary">Price Range:</Typography>
        <Typography variant="caption">
          {rule.minPrice || rule.maxPrice ? `${rule.minPrice || '0'} – ${rule.maxPrice || '∞'}` : '—'}
        </Typography>

        <Typography variant="caption" color="text.secondary">Travel Dates:</Typography>
        <Typography variant="caption">
          {rule.travelDateEffective || rule.travelDateDiscontinue
            ? `${rule.travelDateEffective || '...'} → ${rule.travelDateDiscontinue || '...'}`
            : '—'}
        </Typography>

        <Typography variant="caption" color="text.secondary">Ticketing Dates:</Typography>
        <Typography variant="caption">
          {rule.ticketingDateEffective || rule.ticketingDateDiscontinue
            ? `${rule.ticketingDateEffective || '...'} → ${rule.ticketingDateDiscontinue || '...'}`
            : '—'}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Tooltip
      title={content}
      arrow
      placement="top"
      componentsProps={{
        tooltip: { sx: { bgcolor: 'transparent', p: 0, maxWidth: 'none' } },
      }}
    >
      {children}
    </Tooltip>
  );
}

export default function RuleHealth() {
  const { getExpiredRules, getConflictingRules, bulkDeleteAndResequence, deleteFeeRuleAndResequence } = useRules();
  const [tabValue, setTabValue] = useState(0);
  const [selectedExpired, setSelectedExpired] = useState([]);

  const expiredRules = useMemo(() => getExpiredRules(), [getExpiredRules]);
  const conflicts = useMemo(() => getConflictingRules(), [getConflictingRules]);

  const handleSelectAllExpired = (e) => {
    if (e.target.checked) {
      setSelectedExpired(expiredRules.map(r => r.sequenceNum));
    } else {
      setSelectedExpired([]);
    }
  };

  const handleSelectExpired = (seqNum) => {
    setSelectedExpired(prev =>
      prev.includes(seqNum) ? prev.filter(s => s !== seqNum) : [...prev, seqNum]
    );
  };

  const handleBulkDelete = () => {
    if (selectedExpired.length === 0) return;
    if (window.confirm(`Delete ${selectedExpired.length} expired rules and renumber all remaining rules?`)) {
      bulkDeleteAndResequence(selectedExpired);
      setSelectedExpired([]);
    }
  };

  const handleDeleteSingle = (seqNum) => {
    if (window.confirm(`Delete rule #${seqNum} and shift subsequent rules?`)) {
      deleteFeeRuleAndResequence(seqNum);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Rule Health Check</Typography>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label={`Expired Rules (${expiredRules.length})`} icon={<DeleteSweepIcon />} iconPosition="start" />
        <Tab label={`Conflicts (${conflicts.length})`} icon={<WarningAmberIcon />} iconPosition="start" />
      </Tabs>

      {/* Expired Rules Tab */}
      {tabValue === 0 && (
        <Box>
          <Alert severity="warning" sx={{ mb: 3 }}>
            These rules have a <strong>TravelDateDiscontinue</strong> or <strong>TicketingDateDiscontinue</strong> in the past.
            They will never match any new itinerary. You can safely delete them.
            Deleting will automatically renumber all subsequent rules.
          </Alert>

          {expiredRules.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="success.main">✓ No expired rules found</Typography>
                <Typography color="text.secondary">All rules with date constraints are still active.</Typography>
              </CardContent>
            </Card>
          ) : (
            <>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedExpired.length} of {expiredRules.length} selected
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteSweepIcon />}
                  disabled={selectedExpired.length === 0}
                  onClick={handleBulkDelete}
                >
                  Delete Selected & Resequence
                </Button>
              </Box>

              <Card>
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedExpired.length === expiredRules.length}
                            indeterminate={selectedExpired.length > 0 && selectedExpired.length < expiredRules.length}
                            onChange={handleSelectAllExpired}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Seq#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Carrier</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Origin</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Destination</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Fee</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Travel Expires</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Ticketing Expires</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expiredRules.map(rule => (
                        <RuleDetailTooltip rule={rule} key={rule.sequenceNum}>
                          <TableRow hover selected={selectedExpired.includes(rule.sequenceNum)} sx={{ cursor: 'pointer' }}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedExpired.includes(rule.sequenceNum)}
                                onChange={() => handleSelectExpired(rule.sequenceNum)}
                              />
                            </TableCell>
                            <TableCell><strong>{rule.sequenceNum}</strong></TableCell>
                            <TableCell><Chip label={rule.carrier} size="small" /></TableCell>
                            <TableCell>{rule.origin || '—'}</TableCell>
                            <TableCell>{rule.destination || '—'}</TableCell>
                            <TableCell>
                              {rule.suppress === 'true'
                                ? <Chip label="SUPPRESS" color="error" size="small" />
                                : rule.feePercent != null ? `${rule.feePercent}%` : rule.feeAmount || '—'}
                            </TableCell>
                            <TableCell>
                              {rule.travelDateDiscontinue
                                ? <Chip label={rule.travelDateDiscontinue} size="small" color="error" variant="outlined" />
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {rule.ticketingDateDiscontinue
                                ? <Chip label={rule.ticketingDateDiscontinue} size="small" color="error" variant="outlined" />
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <Button size="small" color="error" onClick={() => handleDeleteSingle(rule.sequenceNum)}>
                                Delete
                              </Button>
                            </TableCell>
                          </TableRow>
                        </RuleDetailTooltip>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </>
          )}
        </Box>
      )}

      {/* Conflicts Tab */}
      {tabValue === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Conflict Detection:</strong> Rules are processed in sequence order and only the first matching rule applies.
            If two rules have overlapping matching criteria (same carrier, origin, destination, cabin, trip type, price source, interline, 
            and overlapping price/date ranges), the rule with the higher sequence number may never be reached.
            <br /><br />
            <strong>Note:</strong> Rules with <em>different price ranges</em> (e.g. USD0–100 vs USD101–200) or 
            <em>non-overlapping date ranges</em> are NOT considered conflicts — they target different price brackets or time windows.
            <br /><br />
            <strong>Hover over any rule card</strong> to see its full details.
          </Alert>

          {conflicts.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="success.main">✓ No conflicting rules detected</Typography>
                <Typography color="text.secondary">All rules have unique matching criteria or non-overlapping price/date ranges.</Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={2}>
              {conflicts.map((conflict, idx) => (
                <Grid item xs={12} key={idx}>
                  <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <WarningAmberIcon color="warning" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Rule #{conflict.ruleA.sequenceNum} shadows Rule #{conflict.ruleB.sequenceNum}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {conflict.reason}
                      </Typography>
                      <Grid container spacing={2}>
                        {/* Rule A - the active/winning rule */}
                        <Grid item xs={12} md={6}>
                          <RuleDetailTooltip rule={conflict.ruleA}>
                            <Box sx={{ p: 2, bgcolor: '#f0faf0', borderRadius: 2, border: '1px solid #c8e6c9', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
                              <Typography variant="caption" color="success.main" sx={{ fontWeight: 700 }}>
                                ✓ ACTIVE — Rule #{conflict.ruleA.sequenceNum} (hover for details)
                              </Typography>
                              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                <Chip label={`Carrier: ${conflict.ruleA.carrier}`} size="small" />
                                {conflict.ruleA.origin && <Chip label={`From: ${conflict.ruleA.origin}`} size="small" />}
                                {conflict.ruleA.destination && <Chip label={`To: ${conflict.ruleA.destination}`} size="small" />}
                                {conflict.ruleA.cabinClass && <Chip label={conflict.ruleA.cabinClass} size="small" />}
                                {conflict.ruleA.tripType && <Chip label={conflict.ruleA.tripType} size="small" />}
                                <Chip label={
                                  conflict.ruleA.suppress === 'true' ? 'SUPPRESS'
                                  : conflict.ruleA.feePercent != null ? `${conflict.ruleA.feePercent}%`
                                  : conflict.ruleA.feeAmount || '—'
                                } size="small" color="success" variant="outlined" />
                                {conflict.ruleA.minPrice && <Chip label={`≥${conflict.ruleA.minPrice}`} size="small" variant="outlined" />}
                                {conflict.ruleA.maxPrice && <Chip label={`≤${conflict.ruleA.maxPrice}`} size="small" variant="outlined" />}
                              </Box>
                            </Box>
                          </RuleDetailTooltip>
                        </Grid>

                        {/* Rule B - the shadowed/unreachable rule */}
                        <Grid item xs={12} md={6}>
                          <RuleDetailTooltip rule={conflict.ruleB}>
                            <Box sx={{ p: 2, bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffe082', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
                              <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700 }}>
                                ⚠ SHADOWED — Rule #{conflict.ruleB.sequenceNum} (hover for details)
                              </Typography>
                              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                <Chip label={`Carrier: ${conflict.ruleB.carrier}`} size="small" />
                                {conflict.ruleB.origin && <Chip label={`From: ${conflict.ruleB.origin}`} size="small" />}
                                {conflict.ruleB.destination && <Chip label={`To: ${conflict.ruleB.destination}`} size="small" />}
                                {conflict.ruleB.cabinClass && <Chip label={conflict.ruleB.cabinClass} size="small" />}
                                {conflict.ruleB.tripType && <Chip label={conflict.ruleB.tripType} size="small" />}
                                <Chip label={
                                  conflict.ruleB.suppress === 'true' ? 'SUPPRESS'
                                  : conflict.ruleB.feePercent != null ? `${conflict.ruleB.feePercent}%`
                                  : conflict.ruleB.feeAmount || '—'
                                } size="small" color="warning" variant="outlined" />
                                {conflict.ruleB.minPrice && <Chip label={`≥${conflict.ruleB.minPrice}`} size="small" variant="outlined" />}
                                {conflict.ruleB.maxPrice && <Chip label={`≤${conflict.ruleB.maxPrice}`} size="small" variant="outlined" />}
                              </Box>
                              <Button
                                size="small" color="error" sx={{ mt: 1 }}
                                onClick={() => handleDeleteSingle(conflict.ruleB.sequenceNum)}
                              >
                                Delete shadowed rule
                              </Button>
                            </Box>
                          </RuleDetailTooltip>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
}
